import { useState, useCallback, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  listOrders,
  getOrderById,
  acceptOrder,
  advanceOrder,
  rejectOrder,
  type OrderDeskFilter,
} from "@veorest/api-client";
import {
  formatCurrency,
  primaryActionForStep,
  type OrderRecord,
  type OrderDetailRecord,
} from "@veorest/utils";
import {
  Phone, MessageCircle, Store, ShoppingBag, ChevronLeft, RefreshCw, WifiOff, Check, X,
} from "lucide-react";
import { useTenant } from "../hooks/useTenant";
import { useI18n } from "../i18n";
import { cacheOrders, getCachedOrdersSync, useBackendReachable, queueAction } from "@veorest/pwa";

/* ── Source icon + label ── */
type SrcMeta = { icon: React.FC<{ size?: number }>; label: string; color: string };
const SOURCE_META: Record<string, SrcMeta> = {
  marketplace_app: { icon: (p) => <Store size={p?.size ?? 14} />, label: "OELOX", color: "#d70f64" },
  storefront: { icon: (p) => <Store size={p?.size ?? 14} />, label: "OELOX", color: "#d70f64" },
  whatsapp_ai: { icon: (p) => <MessageCircle size={p?.size ?? 14} />, label: "WhatsApp", color: "#25D366" },
  whatsapp: { icon: (p) => <MessageCircle size={p?.size ?? 14} />, label: "WhatsApp", color: "#25D366" },
  staff: { icon: (p) => <ShoppingBag size={p?.size ?? 14} />, label: "Local", color: "#6366f1" },
  phone: { icon: (p) => <Phone size={p?.size ?? 14} />, label: "Local", color: "#6366f1" },
};
const SRC_FALLBACK: SrcMeta = { icon: (p) => <ShoppingBag size={p?.size ?? 14} />, label: "Local", color: "#6366f1" };

function sourceMeta(source: string | undefined): SrcMeta {
  if (!source) return SRC_FALLBACK;
  return SOURCE_META[source] ?? SRC_FALLBACK;
}

/* ── Status tabs ── */
const STATUS_TAB_KEYS: OrderDeskFilter[] = ["pending", "kitchen", "ready", "driver", "done"];

function stepLabelKey(step: string): string {
  const m: Record<string, string> = { pending: "orders.pending", kitchen: "orders.kitchen", ready: "orders.ready", driver: "orders.driver", done: "orders.done" };
  return m[step] ?? step;
}

function actionLabelKey(action: string): string {
  const m: Record<string, string> = { accept: "orders.accept", start_cooking: "orders.startCooking", mark_ready: "orders.markReady", mark_done: "orders.complete", dispatch_driver: "orders.driver" };
  return m[action] ?? action;
}

export default function OrdersPage() {
  const { t } = useI18n();
  const { tenant } = useTenant();
  const tenantId = tenant?.id ?? "";
  const currency = tenant?.currency ?? "IQD";
  const qc = useQueryClient();
  const reachable = useBackendReachable();

  const [filter, setFilter] = useState<OrderDeskFilter>("pending");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Optimistic local mutations (order id → new deskStep) for instant UI feedback
  const [optimisticSteps, setOptimisticSteps] = useState<Record<string, string>>({});

  /* ── Data ── */
  const listQuery = useQuery({
    queryKey: ["pos-orders", tenantId, filter],
    queryFn: () => listOrders({ tenantId: tenantId!, filter, search: "" }),
    enabled: Boolean(tenantId),
    refetchInterval: reachable ? 10_000 : false,
    placeholderData: () => {
      const cached = getCachedOrdersSync(tenantId, filter);
      if (cached.length > 0) {
        return { orders: cached as unknown as OrderRecord[], hasMore: false };
      }
      return undefined;
    },
  });
  const detailQuery = useQuery({
    queryKey: ["pos-order-detail", tenantId, selectedId],
    queryFn: () => getOrderById(tenantId!, selectedId!),
    enabled: Boolean(tenantId && selectedId && reachable),
  });

  const orders: OrderRecord[] = useMemo(() => {
    const raw = (listQuery.data?.orders ?? []) as OrderRecord[];
    if (Object.keys(optimisticSteps).length === 0) return raw;
    return raw.map((o) => optimisticSteps[o.id] ? { ...o, deskStep: optimisticSteps[o.id] } : o) as OrderRecord[];
  }, [listQuery.data, optimisticSteps]);
  const detail: OrderDetailRecord | null = detailQuery.data ?? null;

  // Cache orders on successful fetch
  useEffect(() => {
    if (listQuery.data?.orders && listQuery.data.orders.length > 0 && tenantId) {
      cacheOrders(tenantId, listQuery.data.orders.map((o) => ({
        id: o.id, tenant_id: tenantId, displayNumber: o.displayNumber,
        customer_name: o.customer_name, status: o.status, deskStep: o.deskStep,
        total: o.total, source: o.source, fulfillment_type: o.fulfillment_type,
        created_at: o.created_at, cached_at: Date.now(),
      })));
    }
  }, [listQuery.data, tenantId]);

  // Offline detection: query errored AND we have cached data = we're showing stale data
  const isOfflineMode = listQuery.isError && orders.length > 0;
  const isEmpty = !listQuery.isLoading && !listQuery.isError && orders.length === 0;

  const invalidate = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["pos-orders", tenantId] });
    qc.invalidateQueries({ queryKey: ["pos-order-detail", tenantId] });
  }, [qc, tenantId]);

  /* ── Actions (optimistic + offline-queue aware) ── */
  const handleAction = useCallback(async (order: OrderRecord) => {
    const action = primaryActionForStep(order.deskStep, order.fulfillment_type);
    if (action === "none") { setSelectedId(order.id); return; }
    if (action === "dispatch_driver") { setSelectedId(order.id); return; }

    // Compute next deskStep for optimistic UI
    const stepMap: Record<string, string> = {
      accept: "kitchen", start_cooking: "kitchen", mark_ready: "ready",
      mark_done: "done",
    };
    const nextStep = stepMap[action] ?? order.deskStep;

    // Apply optimistic update immediately
    setOptimisticSteps((prev) => ({ ...prev, [order.id]: nextStep }));

    if (!reachable) {
      // Offline: queue the action
      if (action === "accept") {
        queueAction("accept_order", { tenantId: tenantId!, orderId: order.id, prepTime: 15 });
      } else {
        queueAction("advance_order", { tenantId: tenantId!, orderId: order.id, step: action, fulfillmentType: order.fulfillment_type });
      }
      return;
    }

    // Online: execute immediately, rollback on failure
    setBusy(true);
    try {
      if (action === "accept") {
        await acceptOrder(tenantId!, order.id, 15);
      } else {
        await advanceOrder(tenantId!, order.id, action as "mark_ready" | "mark_done" | "start_cooking", order.fulfillment_type);
      }
      invalidate();
    } catch {
      // Rollback optimistic update on failure
      setOptimisticSteps((prev) => {
        const next = { ...prev };
        delete next[order.id];
        return next;
      });
    } finally { setBusy(false); }
  }, [tenantId, reachable, invalidate]);

  const handleDecline = useCallback(async (order: OrderRecord) => {
    // Optimistic: remove from list
    setOptimisticSteps((prev) => ({ ...prev, [order.id]: "declined" }));

    if (!reachable) {
      queueAction("reject_order", { tenantId: tenantId!, orderId: order.id, reason: "declined by staff" });
      if (selectedId === order.id) setSelectedId(null);
      return;
    }

    setBusy(true);
    try {
      await rejectOrder(tenantId!, order.id, "declined by staff");
      invalidate();
      if (selectedId === order.id) setSelectedId(null);
    } catch {
      setOptimisticSteps((prev) => {
        const next = { ...prev };
        delete next[order.id];
        return next;
      });
    } finally { setBusy(false); }
  }, [tenantId, reachable, invalidate, selectedId]);

  if (!tenantId) return <div style={{ padding: 20, color: "var(--text-muted)" }}>Loading…</div>;

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* ── Status tabs ── */}
      <div style={{ display: "flex", gap: 6, padding: "12px 16px", background: "var(--surface-raised)", borderBottom: "1px solid var(--border-subtle)", overflowX: "auto", flexShrink: 0, alignItems: "center" }}>
        {STATUS_TAB_KEYS.map((key) => {
          const count = key === filter && orders.length > 0 ? orders.length : undefined;
          return (
            <button
              key={key}
              onClick={() => { setFilter(key); setSelectedId(null); }}
              style={{
                padding: "10px 20px", borderRadius: "var(--radius-full)", border: "none",
                fontWeight: 700, fontSize: 14, whiteSpace: "nowrap", cursor: "pointer",
                background: filter === key ? "var(--accent)" : "var(--surface-hover)",
                color: filter === key ? "#fff" : "var(--text-secondary)",
                display: "flex", alignItems: "center", gap: 8,
              }}
            >
              {t(stepLabelKey(key))}
              {count != null && <span style={{ background: filter === key ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.08)", padding: "1px 8px", borderRadius: 999, fontSize: 12 }}>{count}</span>}
            </button>
          );
        })}
        <div style={{ flex: 1 }} />
        <button onClick={() => { invalidate(); }} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 8 }}>
          <RefreshCw size={18} className={listQuery.isFetching ? "animate-spin" : ""} />
        </button>
      </div>

      {/* ── Split: list + detail ── */}
      <div style={{ flex: 1, display: "flex", minHeight: 0, overflow: "hidden" }}>
        {/* List */}
        <div style={{
          width: selectedId ? "min(420px, 42%)" : "100%",
          minWidth: selectedId ? 320 : 0,
          borderRight: selectedId ? "1px solid var(--border-subtle)" : "none",
          overflowY: "auto", transition: "width 0.2s",
        }}>
          {listQuery.isLoading && orders.length === 0 ? (
            <div style={{ padding: 32, textAlign: "center", color: "var(--text-muted)" }}>{t("orders.loading")}</div>
          ) : isEmpty ? (
            <div style={{ padding: 60, textAlign: "center", color: "var(--text-muted)" }}>
              <ShoppingBag size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
              <p style={{ fontWeight: 700, fontSize: 15, margin: "0 0 4px" }}>{t("orders.noOrders")}</p>
              <p style={{ fontSize: 12, margin: 0 }}>{t("orders.noOrdersHint")}</p>
            </div>
          ) : (
            <>
              {isOfflineMode && (
                <div style={{ padding: "6px 16px", background: "var(--warning)", color: "#fff", fontSize: 11, fontWeight: 700, textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                  <WifiOff size={12} /> {t("orders.offlineData") || "Offline data — may not reflect latest orders"}
                </div>
              )}
              {orders.filter((o) => optimisticSteps[o.id] !== "declined").map((order) => {
                const src = sourceMeta(order.source);
                const SrcIcon = src.icon;
                const action = primaryActionForStep(order.deskStep, order.fulfillment_type);
                const isSelected = selectedId === order.id;
                return (
                  <div key={order.id} style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                    <div
                      onClick={() => setSelectedId(isSelected ? null : order.id)}
                      style={{
                        display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
                        width: "100%", border: "none",
                        background: isSelected ? "var(--accent-light)" : "var(--surface-raised)",
                        cursor: "pointer", textAlign: "start",
                        borderLeft: isSelected ? "3px solid var(--accent)" : "3px solid transparent",
                      }}
                    >
                      {/* Source icon */}
                      <div style={{
                        width: 40, height: 40, borderRadius: 12,
                        background: src.color + "18", display: "flex",
                        alignItems: "center", justifyContent: "center", flexShrink: 0,
                      }}>
                        <SrcIcon size={18} />
                      </div>
                      {/* Info */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                          <span style={{ fontWeight: 800, fontSize: 14, color: "var(--text-primary)" }}>#{order.displayNumber}</span>
                          <span style={{
                            fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999,
                            background: order.deskStep === "pending" ? "#fef3c7" : order.deskStep === "kitchen" ? "#dbeafe" : order.deskStep === "ready" ? "#d1fae5" : order.deskStep === "driver" ? "#ede9fe" : "#f3f4f6",
                            color: order.deskStep === "pending" ? "#92400e" : order.deskStep === "kitchen" ? "#1e40af" : order.deskStep === "ready" ? "#065f46" : order.deskStep === "driver" ? "#5b21b6" : "#374151",
                          }}>
                            {t(stepLabelKey(order.deskStep))}
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                          <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>{order.customer_name}</span>
                          <span style={{ marginLeft: 8, fontWeight: 700, color: "var(--accent)" }}>
                            {formatCurrency(order.total, currency)}
                          </span>
                        </div>
                      </div>
                      {/* Action buttons — small round circles inline at right */}
                      <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "center" }}>
                        {action !== "none" && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleAction(order); }}
                            disabled={busy}
                            title={t(actionLabelKey(action))}
                            style={{
                              width: 38, height: 38, borderRadius: "50%", border: "none",
                              background: "var(--accent)", color: "#fff",
                              cursor: busy ? "not-allowed" : "pointer",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              opacity: busy ? 0.6 : 1,
                            }}
                          >
                            <Check size={18} strokeWidth={2.5} />
                          </button>
                        )}
                        {order.deskStep === "pending" && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDecline(order); }}
                            disabled={busy}
                            title={t("orders.decline")}
                            style={{
                              width: 38, height: 38, borderRadius: "50%",
                              border: "2px solid var(--accent)", background: "#fff",
                              color: "var(--accent)",
                              cursor: busy ? "not-allowed" : "pointer",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              opacity: busy ? 0.6 : 1,
                            }}
                          >
                            <X size={18} strokeWidth={2.5} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Detail panel */}
        {selectedId && detail && (
          <div style={{ flex: 1, overflowY: "auto", background: "var(--surface-raised)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 18px", borderBottom: "1px solid var(--border-subtle)", position: "sticky", top: 0, background: "var(--surface-raised)", zIndex: 1 }}>
              <button onClick={() => setSelectedId(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-secondary)", padding: 4 }}>
                <ChevronLeft size={20} />
              </button>
              <span style={{ fontWeight: 800, fontSize: 16 }}>{t("orders.title")} #{detail.displayNumber}</span>
              <div style={{ flex: 1 }} />
              {(() => {
                const a = primaryActionForStep(detail.deskStep, detail.fulfillment_type);
                if (a === "none") return null;
                return (
                  <button onClick={() => handleAction(detail)} disabled={busy} style={{
                    padding: "10px 20px", borderRadius: "var(--radius-full)", border: "none",
                    background: "var(--accent)",
                    color: "#fff", fontWeight: 700, fontSize: 14, cursor: busy ? "not-allowed" : "pointer",
                    display: "flex", alignItems: "center", gap: 6,
                  }}>
                    {a === "accept" && <Check size={16} strokeWidth={2.5} />}
                    {t(actionLabelKey(a))}
                  </button>
                );
              })()}
            </div>
            <div style={{ padding: "18px 20px", maxWidth: 700 }}>
              {/* Customer card */}
              <div style={{ marginBottom: 20, padding: 14, background: "var(--surface-muted)", borderRadius: "var(--radius-lg)", display: "flex", gap: 12, alignItems: "center" }}>
                <div style={{ width: 44, height: 44, borderRadius: 12, background: "var(--accent-light)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 16, color: "var(--accent)" }}>
                  {detail.customer_name?.charAt(0) ?? "?"}
                </div>
                <div>
                  <p style={{ margin: 0, fontWeight: 800, fontSize: 16 }}>{detail.customer_name}</p>
                  {detail.customer_phone && <p style={{ margin: "2px 0 0", fontSize: 13, color: "var(--text-secondary)" }}>{detail.customer_phone}</p>}
                </div>
                <div style={{ flex: 1 }} />
                <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 12px", borderRadius: 999, background: sourceMeta(detail.source).color + "18", color: sourceMeta(detail.source).color }}>
                  {sourceMeta(detail.source).label}
                </span>
              </div>

              {/* Delivery address */}
              {detail.customer_address && (
                <div style={{ marginBottom: 20, fontSize: 13, color: "var(--text-secondary)", padding: "0 4px" }}>
                  📍 {detail.customer_address}
                </div>
              )}

              {/* Items */}
              <h3 style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{t("orders.detail.items")} ({detail.item_count})</h3>
              <div style={{ marginBottom: 20 }}>
                {detail.items?.map((item) => (
                  <div key={item.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid var(--border-subtle)", fontSize: 14 }}>
                    <span style={{ fontWeight: 600 }}>{item.quantity}× {item.item_name}</span>
                    <span style={{ fontWeight: 700, color: "var(--text-secondary)" }}>{formatCurrency(item.unit_price * item.quantity, currency)}</span>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div style={{ borderTop: "2px solid var(--border)", paddingTop: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, marginBottom: 6 }}>
                  <span style={{ color: "var(--text-secondary)" }}>{t("orders.detail.subtotal")}</span>
                  <span style={{ fontWeight: 600 }}>{formatCurrency(detail.subtotal, currency)}</span>
                </div>
                {detail.delivery_fee > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, marginBottom: 6 }}>
                    <span style={{ color: "var(--text-secondary)" }}>{t("orders.detail.deliveryFee")}</span>
                    <span style={{ fontWeight: 600 }}>{formatCurrency(detail.delivery_fee, currency)}</span>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 800, fontSize: 18, marginTop: 8, paddingTop: 10, borderTop: "1px solid var(--border-subtle)" }}>
                  <span>{t("orders.detail.total")}</span>
                  <span style={{ color: "var(--accent)" }}>{formatCurrency(detail.total, currency)}</span>
                </div>
              </div>

              {/* Meta footer */}
              <div style={{ marginTop: 20, padding: 12, background: "var(--surface-muted)", borderRadius: "var(--radius-md)", fontSize: 12, color: "var(--text-muted)", display: "flex", flexWrap: "wrap", gap: 20 }}>
                <span>{t("orders.detail.status")}: <strong style={{ color: "var(--text-primary)" }}>{t(stepLabelKey(detail.deskStep))}</strong></span>
                <span>{t("orders.detail.fulfillment")}: <strong style={{ color: "var(--text-primary)" }}>{detail.fulfillment_type}</strong></span>
                <span>{t("orders.detail.created")}: <strong style={{ color: "var(--text-primary)" }}>{new Date(detail.created_at).toLocaleString()}</strong></span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
